import React, { useState, useRef, useEffect } from 'react';
import './CustomMultiSelect.css';
import ClayLabel from '@clayui/label';
import DropDown from '@clayui/drop-down';
import { ClayCheckbox, ClayInput } from '@clayui/form';
import { UseFormRegister, FieldValues, FieldErrors } from 'react-hook-form';

interface Props {
    value: string[];
    handleOnChange: (e: string[]) => void;
    options: {
        key: string;
        name: string;
    }[];
    name: string;
    register: UseFormRegister<FieldValues>;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    errors: FieldErrors<FieldValues>;
}

const CustomMultiSelect: React.FC<Props> = ({
    value,
    handleOnChange,
    options,
    name,
    placeholder,
    disabled,
    errors
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);

    const isChecked = (item: string) => {
        return value.includes(item);
    };

    const handleSelect = (e: string) => {
        if (value.includes(e)) {
            handleOnChange(value.filter((item) => item != e));
        } else {
            setInputValue('');
            handleOnChange([...value, e]);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [wrapperRef]);

    useEffect(() => {
        if (inputValue) {
            setFilteredOptions(
                options.filter((option) =>
                    option.name.toLowerCase().includes(inputValue.toLowerCase())
                )
            );
        } else {
            setFilteredOptions(options);
        }
    }, [inputValue, options]);

    const handleSelectAll = () => {
        if (value.length === options.length) {
          handleOnChange([]);
        } else {
          handleOnChange(options.map((option) => option.key));
        }
      };

    return (
        <div
            ref={wrapperRef}
            className={`custom-multiselect form-group ${disabled ? 'Disabled' : ''}`}
        >
            <div
                className={`search-dropdown-container ${name}-adv-dropdown ${
                    disabled ? 'Disabled' : ''
                }`}
                onClick={() => {
                    !disabled ? setIsOpen(true) : setIsOpen(!isOpen);
                }}
            >
                <ClayInput
                    className="custom_input custom-select"
                    id={name}
                    name={name}
                    disabled={disabled}
                    placeholder={placeholder}
                    autoComplete="off"
                    onChange={(e) => setInputValue(e?.target?.value)}
                    type="text"
                    value={inputValue && inputValue}
                />
            </div>
            {errors[name] && (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                    {errors[name]?.message as string}
                </div>
            )}
            {isOpen ? (
                <div className="search-dropdown-container-menu">
                    <DropDown.ItemList>
                    {name==="delegationList" ?<DropDown.Item>
                        <ClayCheckbox
                        checked={value.length === options.length}
                        onChange={handleSelectAll}
                        label="Select All"
                        disabled={false}
                        className={
                            value.length === options.length
                                ? 'list-item-active'
                                : ''}
                        />
                    </DropDown.Item>:null}
                        {filteredOptions.map((item) => (
                            <DropDown.Item
                                key={item.key}
                                className={
                                    isChecked(item.key)
                                        ? 'list-item-active'
                                        : ''
                                }
                            >
                                <ClayCheckbox
                                    checked={isChecked(item.key)}
                                    onChange={(e) =>
                                        handleSelect(e.target.value)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    value={item.key}
                                    name={name}
                                    label={item.name}
                                />
                            </DropDown.Item>
                        ))}
                    </DropDown.ItemList>
                </div>
            ) : null}
            <div className="my-2">
                {value?.length > 0 &&
                    value.map((key) => {
                        const option = options.find(
                            (option) => option.key === key
                        );
                        return (
                            option && <ClayLabel
                                key={key}
                                displayType="info"
                                className="custom-label"
                                closeButtonProps={ disabled
                                    ? undefined
                                    : {
                                    'aria-label': 'Close',
                                    title: 'Close',
                                    onClick: () =>
                                        handleOnChange(
                                            value.filter((val) => val !== key)
                                        )
                                }}
                            >
                                {option?.name || key}{' '}
                            </ClayLabel>
                        );
                    })}
            </div>
        </div>
    );
};

export default CustomMultiSelect;